import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView } from 'motion/react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FileText, Code2, Monitor, Clock, Users, Shield,
  Zap, Brain, GitBranch, Layers, Palette, Terminal, CheckCircle2,
  ArrowRight, Menu, X, ChevronDown, Sparkles, Play, Star,
  Database, Cpu, Timer, Activity, Lock, Globe, Mail,
} from 'lucide-react';
import { api } from '../api.js';
import { useAuthStore } from '../store/auth.js';
import { applyBranding, loadBrandingFromStorage } from '../utils/branding.js';
import { useT } from '../i18n/useT.js';

function AnimatedCounter({ value, suffix = '', prefix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {prefix}{display.toLocaleString()}{suffix}
    </span>
  );
}

function FloatingOrb({ className }) {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`}
      animate={{ y: [0, -30, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
    </div>
  );
}

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FeatureCard({ icon: Icon, title, desc, delay = 0, gradient = 'from-violet-500 to-purple-600' }) {
  return (
    <FadeIn delay={delay}>
      <div className="group relative h-full rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-300 hover:border-violet-500/30 hover:bg-white/[0.06] hover:-translate-y-1">
        <div className={`mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
          <Icon size={22} className="text-white" />
        </div>
        <h3 className="mb-2 text-white/90" style={{ fontSize: '1rem', fontWeight: 600 }}>{title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{desc}</p>
      </div>
    </FadeIn>
  );
}

function StatCard({ icon: Icon, value, suffix, prefix, label, color }) {
  return (
    <FadeIn>
      <div className="relative rounded-2xl border border-white/[0.08] bg-white/[0.04] p-6 text-center backdrop-blur-sm overflow-hidden group hover:border-white/20 transition-all duration-300">
        <div className={`mx-auto mb-3 inline-flex items-center justify-center w-10 h-10 rounded-xl ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <div className="text-white mb-1" style={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
          <AnimatedCounter value={value} suffix={suffix} prefix={prefix} />
        </div>
        <p className="text-xs text-white/50 uppercase tracking-widest">{label}</p>
      </div>
    </FadeIn>
  );
}

function Step({ number, title, desc, isLast = false }) {
  return (
    <FadeIn delay={Number(number) * 0.1}>
      <div className="relative flex gap-6">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center text-white shrink-0" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {number}
          </div>
          {!isLast && <div className="w-px flex-1 bg-gradient-to-b from-violet-500/50 to-transparent mt-3" />}
        </div>
        <div className="pb-10">
          <h3 className="text-white mb-2" style={{ fontWeight: 600, fontSize: '1.05rem' }}>{title}</h3>
          <p className="text-white/50 text-sm leading-relaxed">{desc}</p>
        </div>
      </div>
    </FadeIn>
  );
}

function CodePreview() {
  const code = `POST /api/render HTTP/1.1
Content-Type: application/json
Authorization: Bearer <token>

{
  "template": "invoice-v2",
  "data": {
    "client": "Acme Corp",
    "items": [...],
    "total": 4200.00
  },
  "output": "pdf"
}

// Response: 200 OK
// Content-Type: application/pdf`;

  return (
    <div className="relative rounded-2xl border border-white/[0.08] bg-[#0d0d1a] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="w-3 h-3 rounded-full bg-red-500/70" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
        <div className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-2 text-white/30 text-xs">render.http</span>
      </div>
      <pre className="p-5 text-xs leading-relaxed overflow-x-auto">
        {code.split('\n').map((line, i) => {
          let color = 'text-white/60';
          if (line.startsWith('POST') || line.startsWith('Content') || line.startsWith('Authorization')) color = 'text-violet-400';
          if (line.startsWith('{') || line.startsWith('}') || line.startsWith('//')) color = 'text-white/30';
          if (line.includes('"')) color = 'text-emerald-400';
          if (line.startsWith('// Response')) color = 'text-blue-400';
          return <div key={i} className={color}>{line || ' '}</div>;
        })}
      </pre>
    </div>
  );
}

function PricingCard({ plan, onAction }) {
  return (
    <FadeIn>
      <div className={`relative h-full rounded-2xl p-px overflow-hidden transition-all duration-300 hover:-translate-y-1 ${plan.featured ? 'shadow-2xl shadow-violet-500/20' : ''}`}
        style={{ background: plan.featured ? 'linear-gradient(135deg, #7c3aed, #4f46e5, #0ea5e9)' : 'rgba(255,255,255,0.08)' }}>
        <div className={`relative h-full rounded-2xl p-6 flex flex-col ${plan.featured ? 'bg-[#0e0b20]' : 'bg-[#0d0d1a]'}`}>
          {plan.badge && (
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs bg-gradient-to-r from-violet-500 to-indigo-500 text-white" style={{ fontWeight: 600 }}>
              {plan.badge}
            </div>
          )}
          <div className="mb-5">
            <h3 className="text-white mb-3" style={{ fontWeight: 600, fontSize: '1.1rem' }}>{plan.name}</h3>
            <div className="flex items-end gap-1">
              <span className="text-white" style={{ fontWeight: 800, fontSize: '2.25rem', letterSpacing: '-0.04em', lineHeight: 1 }}>{plan.price}</span>
              <span className="text-white/40 mb-1 text-sm">{plan.period}</span>
            </div>
          </div>
          <ul className="flex-1 space-y-3 mb-6">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-white/70">
                <CheckCircle2 size={15} className="text-violet-400 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={onAction}
            className={`w-full py-3 rounded-xl text-sm transition-all duration-200 cursor-pointer ${plan.featured
              ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-500 hover:to-indigo-500 shadow-lg shadow-violet-500/25'
              : 'border border-white/15 text-white/80 hover:bg-white/10 hover:border-white/30'}`}
            style={{ fontWeight: 600 }}
          >
            {plan.cta}
          </button>
        </div>
      </div>
    </FadeIn>
  );
}

function planFromPublic(plan) {
  const limits = plan.includedLimits || {};
  const features = plan.features || {};
  const featureLines = [
    limits.rendersMonth != null ? `${Number(limits.rendersMonth).toLocaleString()} renders/month` : null,
    limits.templatesActive === -1 ? 'Unlimited templates' : (limits.templatesActive != null ? `${Number(limits.templatesActive).toLocaleString()} active templates` : null),
    limits.storageGB != null ? `${Number(limits.storageGB).toLocaleString()} GB storage` : null,
    limits.apiCallsPerMinute != null ? `${Number(limits.apiCallsPerMinute).toLocaleString()} API calls/min` : null,
    limits.concurrentRenders != null ? `${Number(limits.concurrentRenders).toLocaleString()} concurrent renders` : null,
    ...Object.entries(features).filter(([, v]) => v === true).map(([k]) => k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toUpperCase())),
  ].filter(Boolean);

  const amount = Number(plan.monthlyPrice || 0);
  return {
    name: plan.name,
    price: amount <= 0 ? 'Free' : `$${amount.toFixed(0)}`,
    period: amount <= 0 ? '' : '/month',
    features: featureLines.slice(0, 12),
    featured: String(plan.code || '').toLowerCase() === 'pro',
    cta: amount <= 0 ? 'Start for free' : 'Get started',
    badge: String(plan.code || '').toLowerCase() === 'pro' ? 'Most Popular' : undefined,
  };
}

const FALLBACK_PLANS = [
  {
    name: 'Starter',
    price: '$0',
    period: '/month',
    features: ['100 renders/month', '5 active templates', '1 user', '1 GB storage', 'Community support', 'Basic API access'],
    featured: false,
    cta: 'Start for free',
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    features: [
      '10,000 renders/month',
      '300 active templates',
      '25 GB storage',
      '300 API calls/min',
      '8 concurrent renders',
      '100 AI generations/month',
      '100 template versions',
      'Priority queue',
      'Studio + Visual Builder',
      'Custom branding',
      'Custom scripts',
      'Priority support',
    ],
    featured: true,
    cta: 'Get started',
    badge: 'Most Popular',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    features: ['Unlimited renders', 'Unlimited templates', 'Unlimited users', 'Dedicated storage', 'SLA guarantee', '24/7 support', 'Custom integrations'],
    featured: false,
    cta: 'Contact sales',
  },
];

const FEATURES = [
  { icon: FileText, titleKey: 'landing.features.handlebarsTitle', titleFallback: 'Handlebars Templates', descKey: 'landing.features.handlebarsDesc', descFallback: 'Design documents with HTML + Handlebars. Built-in helpers, partials, and dynamic data binding.', gradient: 'from-violet-500 to-purple-600' },
  { icon: Code2, titleKey: 'landing.features.scriptsTitle', titleFallback: 'Custom Scripts', descKey: 'landing.features.scriptsDesc', descFallback: 'Run JavaScript before or after render. Transform data, add watermarks, or validate content.', gradient: 'from-blue-500 to-cyan-500' },
  { icon: Monitor, titleKey: 'landing.features.previewTitle', titleFallback: 'Real-time Studio', descKey: 'landing.features.previewDesc', descFallback: 'Visual drag-and-drop builder. Edit HTML, CSS and JSON data and see live changes instantly.', gradient: 'from-emerald-500 to-teal-500' },
  { icon: Zap, titleKey: 'landing.features.apiTitle', titleFallback: 'Full REST API', descKey: 'landing.features.apiDesc', descFallback: 'Generate PDFs from any language. Endpoints for templates, assets, scripts and async jobs.', gradient: 'from-amber-500 to-orange-500' },
  { icon: Clock, titleKey: 'landing.features.versioningTitle', titleFallback: 'Template Versioning & Publishing', descKey: 'landing.features.versioningDesc', descFallback: 'Track every change, compare versions, and publish stable templates with one click.', gradient: 'from-pink-500 to-rose-500' },
  { icon: Brain, titleKey: 'landing.features.aiTitle', titleFallback: 'AI Generation', descKey: 'landing.features.aiDesc', descFallback: 'AI-powered document generations. Intelligent content and layout suggestions.', gradient: 'from-indigo-500 to-violet-600' },
  { icon: Layers, titleKey: 'landing.features.visualTitle', titleFallback: 'Visual Builder', descKey: 'landing.features.visualDesc', descFallback: 'Drag-and-drop interface to build templates visually. No code required to get started.', gradient: 'from-cyan-500 to-blue-500' },
  { icon: Palette, titleKey: 'landing.features.priorityQueueTitle', titleFallback: 'Priority Queue', descKey: 'landing.features.priorityQueueDesc', descFallback: 'Render critical documents first with configurable priority levels and queue control.', gradient: 'from-fuchsia-500 to-pink-500' },
  { icon: Users, titleKey: 'landing.features.collaborationTitle', titleFallback: 'Team Collaboration', descKey: 'landing.features.collaborationDesc', descFallback: 'Invite teammates, share templates, and review changes together before publishing.', gradient: 'from-teal-500 to-emerald-600' },
];

const SECURITY_ITEMS = [
  { icon: Lock, key: 'landing.securityItems.jwt', fallback: 'JWT authentication with short-lived tokens' },
  { icon: Shield, key: 'landing.securityItems.roles', fallback: 'Roles and permissions: admin, editor, viewer' },
  { icon: Globe, key: 'landing.securityItems.isolation', fallback: 'Data isolation by tenant and user' },
  { icon: Terminal, key: 'landing.securityItems.sandbox', fallback: 'Script execution in controlled sandbox' },
  { icon: CheckCircle2, key: 'landing.securityItems.tls', fallback: 'HTTPS/TLS in all communications' },
  { icon: Activity, key: 'landing.securityItems.audit', fallback: 'Real-time monitoring and audit logs' },
];

const NAV_LINKS = [
  { id: 'features', key: 'landing.nav.features', fallback: 'Features' },
  { id: 'how-it-works', key: 'landing.nav.how', fallback: 'How it works' },
  { id: 'security', key: 'landing.nav.security', fallback: 'Security' },
  { id: 'pricing', key: 'landing.nav.pricing', fallback: 'Pricing' },
];

function isSvgLogo(src) {
  if (!src) return false;
  const value = String(src).toLowerCase();
  return value.includes('image/svg+xml') || value.endsWith('.svg') || value.includes('.svg?');
}

export default function LandingPage() {
  const t = useT();
  const token = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [branding, setBranding] = useState(() => loadBrandingFromStorage());
  const [publicPlans, setPublicPlans] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    let isMounted = true;

    const syncBranding = async () => {
      if (!token) {
        const local = loadBrandingFromStorage();
        if (isMounted) setBranding(local);
        return;
      }

      try {
        const serverBranding = await api.getAdminSettings();
        const applied = applyBranding({ ...loadBrandingFromStorage(), ...serverBranding });
        if (isMounted) setBranding(applied);
      } catch {
        const local = loadBrandingFromStorage();
        if (isMounted) setBranding(local);
      }
    };

    syncBranding();
    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    let mounted = true;
    const loadPublicPlans = async () => {
      try {
        const res = await api.getPublicPlans();
        if (!mounted) return;
        const normalized = (res.items || []).map(planFromPublic);
        setPublicPlans(normalized);
      } catch {
        if (mounted) setPublicPlans([]);
      }
    };
    loadPublicPlans();
    return () => { mounted = false; };
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  }, []);

  const handleCta = useCallback(() => {
    if (token) navigate('/templates');
    else navigate('/login');
  }, [navigate, token]);

  const handleLogout = useCallback(() => {
    logout();
    setMenuOpen(false);
  }, [logout]);

  const plans = publicPlans.length ? publicPlans : FALLBACK_PLANS;

  return (
    <div className="landing-v2 min-h-screen" style={{ background: '#07070f', color: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(7,7,15,0.85)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => scrollTo('hero')}>
            {branding.logoIconUrl ? (
              isSvgLogo(branding.logoIconUrl) ? (
                <object
                  data={branding.logoIconUrl}
                  type="image/svg+xml"
                  aria-label={branding.logoText || branding.appName || 'Logo'}
                  style={{ height: 32, width: 'auto', minWidth: 32, maxWidth: 220, pointerEvents: 'none' }}
                >
                  <img
                    src={branding.logoIconUrl}
                    alt={branding.logoText || branding.appName || 'Logo'}
                    style={{ maxHeight: 32, width: 'auto', objectFit: 'contain' }}
                  />
                </object>
              ) : (
                <img
                  src={branding.logoIconUrl}
                  alt={branding.logoText || branding.appName || 'Logo'}
                  style={{ maxHeight: 32, width: 'auto', objectFit: 'contain' }}
                />
              )
            ) : (
              <>
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <FileText size={16} className="text-white" />
                </div>
                <span className="text-white" style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                  {branding.logoText || branding.appName || 'PDFStudio'}
                </span>
              </>
            )}
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="bg-transparent border-0 px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-200 cursor-pointer"
                style={{ fontWeight: 500 }}
              >
                {t(link.key, link.fallback)}
              </button>
            ))}
            <Link to="/referencia-api" className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-200">{t('landing.nav.apiReference', 'API Reference')}</Link>
            <Link to="/documentacion" className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-200">{t('landing.nav.docs', 'Docs')}</Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {token ? (
              <>
                <button onClick={() => navigate('/templates')} className="bg-transparent border-0 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer" style={{ fontWeight: 500 }}>
                  {t('landing.dashboard', 'Dashboard')}
                </button>
                <button onClick={handleLogout} className="px-4 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 transition-all duration-200 shadow-lg shadow-violet-500/25 cursor-pointer" style={{ fontWeight: 600 }}>
                  {t('landing.logout', 'Sign Out')}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="bg-transparent border-0 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer" style={{ fontWeight: 500 }}>
                  {t('landing.login', 'Sign In')}
                </button>
                <button onClick={handleCta} className="px-4 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 transition-all duration-200 shadow-lg shadow-violet-500/25 cursor-pointer" style={{ fontWeight: 600 }}>
                  {t('landing.startFree', 'Start Free')}
                </button>
              </>
            )}
          </div>

          <button
            className="bg-transparent border-0 md:hidden p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden border-t border-white/[0.06] px-4 py-4 space-y-1"
            style={{ background: 'rgba(7,7,15,0.98)', backdropFilter: 'blur(20px)' }}
          >
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="bg-transparent border-0 w-full text-left px-4 py-3 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-all cursor-pointer"
                style={{ fontWeight: 500 }}
              >
                {t(link.key, link.fallback)}
              </button>
            ))}
            <Link to="/referencia-api" className="block w-full text-left px-4 py-3 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.06]">{t('landing.nav.apiReference', 'API Reference')}</Link>
            <Link to="/documentacion" className="block w-full text-left px-4 py-3 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.06]">{t('landing.nav.docs', 'Docs')}</Link>
            <div className="pt-2 flex flex-col gap-2">
              {token ? (
                <>
                  <button onClick={() => navigate('/templates')} className="w-full py-3 rounded-lg text-sm text-white/70 border border-white/15 hover:bg-white/10 transition-all cursor-pointer" style={{ fontWeight: 500 }}>{t('landing.dashboard', 'Dashboard')}</button>
                  <button onClick={handleLogout} className="w-full py-3 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 cursor-pointer" style={{ fontWeight: 600 }}>{t('landing.logout', 'Sign Out')}</button>
                </>
              ) : (
                <>
                  <button onClick={() => navigate('/login')} className="w-full py-3 rounded-lg text-sm text-white/70 border border-white/15 hover:bg-white/10 transition-all cursor-pointer" style={{ fontWeight: 500 }}>{t('landing.login', 'Sign In')}</button>
                  <button onClick={handleCta} className="w-full py-3 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 cursor-pointer" style={{ fontWeight: 600 }}>{t('landing.startFree', 'Start Free')}</button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </header>

      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
        <GridBackground />
        <FloatingOrb className="w-[600px] h-[600px] bg-violet-600 -top-48 -right-48" />
        <FloatingOrb className="w-[500px] h-[500px] bg-indigo-600 -bottom-32 -left-32" />
        <FloatingOrb className="w-[300px] h-[300px] bg-purple-600 top-1/2 right-1/4" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 mb-6"
              >
                <Sparkles size={13} className="text-violet-400" />
                <span className="text-xs text-violet-300" style={{ fontWeight: 600 }}>{t('landing.nowWithAi', 'Now with AI generation')}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mb-5 text-white"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 3.6rem)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.04em' }}
              >
                {t('landing.hero.generate', 'Generate')}{' '}
                <span
                  style={{
                    background: 'linear-gradient(135deg, #a78bfa, #818cf8, #38bdf8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('landing.hero.professionalPdfs', 'professional PDFs')}
                </span>{' '}
                {t('landing.hero.atAnyScale', 'at any scale')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mb-8 text-white/55 leading-relaxed"
                style={{ fontSize: '1.1rem', maxWidth: '500px' }}
              >
                {t('landing.hero.subtitleV2', 'Design templates with Handlebars, run custom scripts, and produce production-ready PDF documents through a simple REST API. Powered by AI.')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="flex flex-wrap gap-3 mb-10"
              >
                <button
                  onClick={handleCta}
                  className="group flex items-center gap-2 px-6 py-3.5 rounded-xl text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 transition-all duration-200 shadow-xl shadow-violet-500/30 cursor-pointer"
                  style={{ fontWeight: 600, fontSize: '0.95rem' }}
                >
                  {t('landing.startFree', 'Start Free')}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-white/80 border border-white/15 hover:border-white/30 hover:bg-white/[0.06] transition-all duration-200 cursor-pointer"
                  style={{ fontWeight: 500, fontSize: '0.95rem' }}
                  onClick={() => scrollTo('how-it-works')}
                >
                  <Play size={14} className="text-violet-400" />
                  {t('landing.seeHow', 'See how it works')}
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="flex items-center gap-4"
              >
                <div className="flex -space-x-2">
                  {['#7c3aed', '#4f46e5', '#0ea5e9', '#10b981'].map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-[#07070f] flex items-center justify-center text-xs text-white" style={{ background: c, fontWeight: 700 }}>
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-amber-400 fill-amber-400" />)}
                  </div>
                  <p className="text-white/40 text-xs">{t('landing.trustedBy', 'Trusted by 2,000+ developers')}</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="hidden lg:block"
            >
              <CodePreview />
              <motion.div
                className="mt-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-10 rounded bg-gradient-to-br from-violet-500/30 to-indigo-500/30 border border-violet-500/20 flex items-center justify-center">
                    <FileText size={14} className="text-violet-400" />
                  </div>
                  <div>
                    <div className="text-white/80 text-xs mb-1" style={{ fontWeight: 600 }}>invoice_acme_2024.pdf</div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-emerald-400 text-xs">Generated in 380ms</span>
                    </div>
                  </div>
                  <div className="ml-auto text-white/30 text-xs">2.4 MB</div>
                </div>
                <div className="space-y-1.5">
                  {['w-full', 'w-4/5', 'w-3/5', 'w-4/5', 'w-2/3'].map((w, i) => (
                    <div key={i} className={`h-1.5 rounded-full bg-white/[0.06] ${w}`} />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 cursor-pointer"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          onClick={() => scrollTo('stats')}
        >
          <span className="text-white/30 text-xs">{t('landing.scroll', 'Scroll')}</span>
          <ChevronDown size={16} className="text-white/30" />
        </motion.div>
      </section>

      <section id="stats" className="py-20 relative overflow-hidden border-y border-white/[0.04]" style={{ background: 'rgba(139,92,246,0.03)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-12">
            <h2 className="text-white mb-3" style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.03em' }}>
              {t('landing.stats.title', 'Built for scale')}
            </h2>
            <p className="text-white/45 text-sm">{t('landing.stats.subtitle', 'Everything included in the Pro plan')}</p>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard icon={Activity} value={10000} suffix="+" label={t('landing.stats.renders', 'Renders / month')} color="bg-gradient-to-br from-violet-500 to-purple-600" />
            <StatCard icon={Database} value={25} suffix=" GB" label={t('landing.stats.storage', 'Storage')} color="bg-gradient-to-br from-blue-500 to-cyan-600" />
            <StatCard icon={FileText} value={300} label={t('landing.stats.templates', 'Active templates')} color="bg-gradient-to-br from-emerald-500 to-teal-600" />
            <StatCard icon={Zap} value={300} suffix="/min" label={t('landing.stats.apiCalls', 'API calls')} color="bg-gradient-to-br from-amber-500 to-orange-500" />
            <StatCard icon={Cpu} value={8} label={t('landing.stats.concurrent', 'Concurrent renders')} color="bg-gradient-to-br from-pink-500 to-rose-600" />
            <StatCard icon={Brain} value={100} suffix="/mo" label={t('landing.stats.ai', 'AI generations')} color="bg-gradient-to-br from-indigo-500 to-violet-600" />
            <StatCard icon={GitBranch} value={100} label={t('landing.stats.versions', 'Template versions')} color="bg-gradient-to-br from-cyan-500 to-blue-600" />
            <StatCard icon={Timer} value={380} suffix="ms" label={t('landing.stats.avg', 'Avg render time')} color="bg-gradient-to-br from-fuchsia-500 to-pink-600" />
          </div>
        </div>
      </section>

      <section id="features" className="py-24 relative overflow-hidden">
        <GridBackground />
        <FloatingOrb className="w-[500px] h-[500px] bg-indigo-600 top-0 right-0 opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 mb-4">
              <Sparkles size={12} className="text-violet-400" />
              <span className="text-xs text-violet-300" style={{ fontWeight: 600 }}>{t('landing.sections.featuresSubtitle', 'Everything you need to generate reports at scale.')}</span>
            </div>
            <h2 className="text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
              {t('landing.featuresHeadline', 'All the power, none of the complexity')}
            </h2>
            <p className="text-white/50 mx-auto" style={{ maxWidth: '520px', lineHeight: 1.7 }}>
              {t('landing.featuresSubHeadline', 'From design to delivery, everything you need to generate reports at scale.')}
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <FeatureCard key={i} icon={f.icon} title={t(f.titleKey, f.titleFallback)} desc={t(f.descKey, f.descFallback)} gradient={f.gradient} delay={i * 0.05} />
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-24 relative overflow-hidden" style={{ background: 'rgba(139,92,246,0.03)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 mb-4">
              <Play size={12} className="text-indigo-400" />
              <span className="text-xs text-indigo-300" style={{ fontWeight: 600 }}>{t('landing.howBadge', 'Simple workflow')}</span>
            </div>
            <h2 className="text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
              {t('landing.sections.howTitle', 'How it works')}
            </h2>
            <p className="text-white/50" style={{ maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
              {t('landing.sections.howSubtitle', 'Three steps to your first reports.')}
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Step
                number="1"
                title="Design your template"
                desc="Open Studio, create a template with HTML + Handlebars, and add CSS styles. Use the Visual Builder for drag-and-drop layout or write code directly. Upload logos and images as assets."
              />
              <Step
                number="2"
                title="Connect your data"
                desc="Send the JSON you need: invoices, financial reports, inventory lists. Helpers and custom scripts transform and enrich your data before rendering."
              />
              <Step
                number="3"
                title="Generate and download"
                isLast
                desc="Call POST /api/render and receive your PDF instantly. Schedule recurring jobs with cron expressions or trigger via webhook."
              />
            </div>

            <FadeIn delay={0.2}>
              <div className="space-y-4">
                <div className="rounded-2xl border border-white/[0.08] overflow-hidden" style={{ background: '#0d0d1a' }}>
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]" style={{ background: 'rgba(255,255,255,0.02)' }}>
                    <div className="w-3 h-3 rounded-full bg-red-500/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                    <span className="ml-2 text-white/30 text-xs">PDFStudio — invoice-template.hbs</span>
                  </div>
                  <div className="grid grid-cols-5">
                    <div className="col-span-1 border-r border-white/[0.06] p-3 space-y-2">
                      {['Template', 'Assets', 'Script', 'Preview'].map((item, i) => (
                        <div key={item} className={`text-xs py-2 px-2 rounded-lg cursor-pointer transition-all ${i === 3 ? 'bg-violet-500/20 text-violet-400' : 'text-white/30 hover:text-white/60'}`} style={{ fontWeight: i === 3 ? 600 : 400 }}>{item}</div>
                      ))}
                    </div>
                    <div className="col-span-2 p-3 border-r border-white/[0.06]">
                      {[
                        { text: '<div class="invoice">', color: 'text-blue-400' },
                        { text: '  {{#each items}}', color: 'text-violet-400' },
                        { text: '    <p>{{name}}</p>', color: 'text-emerald-400' },
                        { text: '  {{/each}}', color: 'text-violet-400' },
                        { text: '</div>', color: 'text-blue-400' },
                      ].map((line, i) => (
                        <div key={i} className={`text-xs font-mono ${line.color} leading-5`}>{line.text}</div>
                      ))}
                    </div>
                    <div className="col-span-2 p-3">
                      <div className="text-xs text-white/20 mb-2">Preview</div>
                      <div className="rounded-lg bg-white/[0.04] p-2 space-y-1.5">
                        {[80, 65, 90, 55, 70].map((w, i) => (
                          <div key={i} className="h-1.5 rounded-full bg-white/10" style={{ width: `${w}%` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                    <Zap size={16} className="text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-emerald-400 text-xs mb-0.5" style={{ fontWeight: 600 }}>PDF generated successfully</div>
                    <div className="text-white/40 text-xs">invoice_acme_2024.pdf · 2.4 MB · 380ms</div>
                  </div>
                  <div className="text-xs text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded-lg" style={{ fontWeight: 600 }}>200 OK</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section id="security" className="py-24 relative overflow-hidden">
        <FloatingOrb className="w-[600px] h-[600px] bg-violet-700 bottom-0 left-1/2 -translate-x-1/2 opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 mb-5">
                <Shield size={12} className="text-emerald-400" />
                <span className="text-xs text-emerald-300" style={{ fontWeight: 600 }}>Enterprise security</span>
              </div>
              <h2 className="text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
                Your data is always protected
              </h2>
              <p className="text-white/50 mb-8 leading-relaxed">
                Built with security at its core. Every request is authenticated, every workspace is isolated, and every script runs in a controlled sandbox.
              </p>
              <ul className="space-y-4">
                {SECURITY_ITEMS.map((item, i) => (
                  <FadeIn key={i} delay={i * 0.07}>
                    <li className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                        <item.icon size={15} className="text-emerald-400" />
                      </div>
                      <span className="text-white/70 text-sm">{t(item.key, item.fallback)}</span>
                    </li>
                  </FadeIn>
                ))}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} className="flex justify-center">
              <div className="relative w-64 h-64">
                {[1, 2, 3].map((ring) => (
                  <motion.div
                    key={ring}
                    className="absolute inset-0 rounded-full border border-violet-500/20"
                    style={{ inset: `${(ring - 1) * 30}px` }}
                    animate={{ scale: [1, 1.02, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 3, delay: ring * 0.5, repeat: Infinity }}
                  />
                ))}
                <div className="absolute inset-12 rounded-full bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/30 flex items-center justify-center">
                  <Shield size={60} className="text-violet-400" strokeWidth={1} />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 relative overflow-hidden" style={{ background: 'rgba(139,92,246,0.03)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 mb-4">
              <Star size={12} className="text-violet-400" />
              <span className="text-xs text-violet-300" style={{ fontWeight: 600 }}>{t('landing.pricingBadge', 'Transparent pricing')}</span>
            </div>
            <h2 className="text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
              {t('landing.pricingHeadline', 'Plans that grow with you')}
            </h2>
            <p className="text-white/50" style={{ maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
              {t('landing.sections.pricingSubtitle', 'Choose the plan that best fits your needs.')}
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {plans.map((plan, i) => (
              <PricingCard key={i} plan={plan} onAction={handleCta} />
            ))}
          </div>

          <FadeIn className="text-center mt-10">
            <p className="text-white/30 text-sm">{t('landing.pricingFootnote', 'All plans include SSL, 99.9% uptime SLA, and GDPR compliance')}</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <FloatingOrb className="w-[700px] h-[700px] bg-violet-600 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-15" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <div className="rounded-3xl border border-white/[0.08] p-12 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(79,70,229,0.15), rgba(14,165,233,0.08))' }}>
              <div className="absolute inset-0 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.05), transparent)' }} />
              <div className="relative">
                <h2 className="text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
                  {t('landing.sections.ctaTitle', 'Ready to automate your reports?')}
                </h2>
                <p className="text-white/50 mb-8 mx-auto" style={{ maxWidth: '440px', lineHeight: 1.7 }}>
                  {t('landing.sections.ctaSubtitle', 'Create your free account and generate your first PDF in less than 5 minutes.')}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button onClick={handleCta} className="group flex items-center gap-2 px-8 py-4 rounded-xl text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 transition-all duration-200 shadow-xl shadow-violet-500/30 cursor-pointer" style={{ fontWeight: 600 }}>
                    {t('landing.createFree', 'Create free account')}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button onClick={() => navigate('/documentacion')} className="flex items-center gap-2 px-8 py-4 rounded-xl text-white/80 border border-white/15 hover:border-white/30 hover:bg-white/[0.06] transition-all duration-200 cursor-pointer" style={{ fontWeight: 500 }}>
                    <Mail size={16} />
                    {t('landing.footer.support', 'Support')}
                  </button>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <FileText size={16} className="text-white" />
                </div>
                <span className="text-white" style={{ fontWeight: 700, fontSize: '1rem' }}>
                  {branding.logoText || branding.appName || 'PDFStudio'}
                </span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed">{t('landing.footer.tagline', 'Professional PDF report generation.')}</p>
            </div>

            <div>
              <h4 className="text-white/80 text-sm mb-4" style={{ fontWeight: 600 }}>{t('landing.footer.product', 'Product')}</h4>
              <ul className="space-y-2.5">
                <li><button onClick={() => scrollTo('features')} className="bg-transparent border-0 text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.nav.features', 'Features')}</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="bg-transparent border-0 text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.nav.pricing', 'Pricing')}</button></li>
                <li><Link to="/referencia-api" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.nav.apiReference', 'API Reference')}</Link></li>
                <li><Link to="/documentacion" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.footer.docs', 'Documentation')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white/80 text-sm mb-4" style={{ fontWeight: 600 }}>{t('landing.footer.legal', 'Legal')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/terminos-de-servicio" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.footer.terms', 'Terms of service')}</Link></li>
                <li><Link to="/politica-de-privacidad" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.footer.privacy', 'Privacy policy')}</Link></li>
                <li><Link to="/aviso-legal" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.footer.notice', 'Legal notice')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white/80 text-sm mb-4" style={{ fontWeight: 600 }}>{t('landing.footer.support', 'Support')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/documentacion" className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.footer.docs', 'Documentation')}</Link></li>
                <li><a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} className="text-white/40 text-sm hover:text-white/70 transition-colors">{t('landing.nav.pricing', 'Pricing')}</a></li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-white/[0.04] gap-4">
            <p className="text-white/30 text-xs">{t('landing.footer.copyright', '© {{year}} Company name. All rights reserved.').replace('{{year}}', String(new Date().getFullYear())).replace('Company name', branding.appName || branding.logoText || 'PDFStudio')}</p>
            <div className="flex items-center gap-4">
              {['Twitter', 'GitHub', 'Discord'].map((s) => (
                <a key={s} href="#" className="text-white/30 text-xs hover:text-white/60 transition-colors">{s}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
